import { createContext, Dispatch, SetStateAction, useEffect, useState } from 'react'
import qs from 'qs'
import {
  ID,
  QueryFilter,
  QueryResponseContextProps,
  QueryResponseContextPropsSemPaginacao,
  QueryState,
} from './models'
import { DocumentoPessoaType } from '../enums'
import { IAvaliacoesSelect } from '../../../app/modules/apps/avaliacoes/avaliacoes-pendentes-list/core/_models'
import { ArquivoDto } from '../../../app/modules/apps/atendimento/core/_models'
import 'pdfjs-dist/build/pdf.worker.mjs'
import { EnderecoList } from '../../../app/modules/apps/empresa/empresas-list/core/_models'
import { IRadioOption } from '../../../app/components'
import { TFunction } from '_metronic/i18n/Metronici18n'

export interface DateValidationResult {
  error: string
  limits: {
    startMin?: string
    startMax?: string
    endMin?: string
    endMax?: string
  }
}

export type FieldType = 'input' | 'select' | 'array'

type ValidateDateRangeOptions = {
  maxRangeDays?: number // undefined = sem limite
}

function createResponseContext<T>(initialState: QueryResponseContextProps<T>) {
  return createContext(initialState)
}

function createResponseContextSemPaginacao<T>(
  initialState: QueryResponseContextPropsSemPaginacao<T>
) {
  return createContext(initialState)
}

function isNotEmpty(obj: unknown) {
  return obj !== undefined && obj !== null && obj !== ''
}

export const stringifyQueryFilters = (filters?: QueryFilter[]): string => {
  if (!filters?.length) return ''

  return filters
    .filter(
      (f): f is QueryFilter =>
        Boolean(f.field) && Boolean(f.operator) && f.value !== undefined && f.value !== null
    )
    .map((f) => `${f.field}${f.operator}${encodeURIComponent(String(f.value))}`)
    .join('&')
}

// Example: page=1&size=10&sort=id&order=desc&search=a&filter_name=a&filter_online=false
function stringifyRequestQuery(state: QueryState<unknown>): string {
  const pagination = qs.stringify(state, { filter: ['page', 'size'], skipNulls: true })

  const uiSort = state.sort ? [{ field: state.sort, order: state.order ?? 'asc' }] : []
  const fixedSort = (state.fixedSort ?? []).filter((f) => f.field !== state.sort)

  const sort = [...uiSort, ...fixedSort].map((s) => `sort=${s.field},${s.order}`).join('&')

  const search = isNotEmpty(state.search)
    ? qs.stringify(state, { filter: ['search'], skipNulls: true })
    : ''

  const filter = state.filter
    ? Object.entries(state.filter as object)
        .filter((obj) => isNotEmpty(obj[1]))
        .map((obj) => `filter_${obj[0]}=${obj[1] ?? ''}`) // Ensures value is a string
        .join('&')
    : ''

  const filters = stringifyQueryFilters(state.filters)

  return [filters, pagination, sort, search, filter].filter(Boolean).join('&')
}

export function stringifyRequestQueryResumo(state: QueryState): string {
  const newState = { ...state, search: state.search?.replace(/estado:[^,]*,?/, '') } // remover estado

  const pagination = qs.stringify(newState, { filter: ['page', 'size'], skipNulls: true })
  const sort = newState.sort ? `sort=${newState.sort}%2C${newState.order ?? ''}` : null
  const search = isNotEmpty(newState.search)
    ? qs.stringify(newState, { filter: ['search'], skipNulls: true })
    : ''

  const filter = newState.filter
    ? Object.entries(newState.filter as object)
        .filter((obj) => isNotEmpty(obj[1]))
        .map((obj) => `filter_${obj[0]}=${obj[1] ?? ''}`)
        .join('&')
    : ''

  return [pagination, sort, search, filter].filter(Boolean).join('&')
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function parseRequestQuery(query: string): QueryState {
  const cache: unknown = qs.parse(query)
  return cache as QueryState
}

function calculatedGroupingIsDisabled<T>(isLoading: boolean, data: Array<T> | undefined): boolean {
  if (isLoading) {
    return true
  }

  return !data || !data.length
}

function calculateIsAllDataSelected<T>(
  data: Array<T> | undefined,
  selected: Array<ID | IAvaliacoesSelect>
): boolean {
  if (!data) {
    return false
  }

  return data.length > 0 && data.length === selected.length
}

function groupingOnSelect(
  id: ID,
  selected: Array<ID>,
  setSelected: Dispatch<SetStateAction<Array<ID>>>
) {
  if (!id) {
    return
  }

  if (selected.includes(id)) {
    setSelected(selected.filter((itemId) => itemId !== id))
  } else {
    const updatedSelected = [...selected]
    updatedSelected.push(id)
    setSelected(updatedSelected)
  }
}

function groupingOnSelectAll<T>(
  isAllSelected: boolean,
  setSelected: Dispatch<SetStateAction<Array<ID>>>,
  data?: Array<T & { id?: ID }>
) {
  if (isAllSelected) {
    setSelected([])
    return
  }

  if (!data || !data.length) {
    return
  }

  setSelected(data.filter((item) => item.id).map((item) => item.id))
}

export function useDebounce<T>(value: T, delay = 500): T {
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])
  return debouncedValue
}

function getParameterByName(name: string, url: string) {
  name = name.replace(/[[\]]/g, '\\$&')
  var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
    results = regex.exec(url)
  if (!results) return null
  if (!results[2]) return ''
  return decodeURIComponent(results[2].replace(/\+/g, ' '))
}

export const buildQueryString = (params: Record<string, any>) => {
  return Object.entries(params)
    .filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&')
}

/**
 * Detecta iPhone/iPod/iPad clássicos e também iPadOS recente, cujo user-agent
 * reporta "Macintosh". A guarda `navigator.maxTouchPoints > 1` evita classificar
 * macOS desktop (sem toque) ou laptops Windows com toque (que reportam "Windows",
 * não "Macintosh") como iOS.
 */
export const isIOS = (): boolean => {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || navigator.vendor || ''
  if (/iphone|ipod|ipad/i.test(ua)) return true
  // iPadOS recente: UA de "Macintosh" + suporte a toque.
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1
}

/**
 * Abre uma aba em branco de forma SÍNCRONA dentro do gesto de toque, para que o
 * iOS Safari preserve a "user activation" e não bloqueie a navegação posterior
 * (após o `await` da API). Retorna o handle da aba ou `null` se o pop-up foi
 * bloqueado. Não usa `noopener` (precisamos do handle); zera `win.opener` por
 * segurança contra tabnabbing.
 */
export const abrirAbaCarregando = (): Window | null => {
  const win = window.open('', '_blank')
  if (win) {
    win.opener = null
    try {
      win.document.write(
        '<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">' +
          '<title>Carregando documento...</title></head>' +
          '<body style="font-family:sans-serif;color:#333;padding:24px">' +
          'Carregando documento...</body></html>'
      )
    } catch {
      // Alguns navegadores bloqueiam o document.write da aba; ignorar é seguro,
      // o importante é o handle para redirecionar depois.
    }
  }
  return win
}

/**
 * Redireciona uma aba previamente aberta (via abrirAbaCarregando) para o arquivo.
 * - Se `arquivo.url` estiver presente, navega direto para a URL do backend.
 * - Caso contrário, gera um blob a partir do `contentBase64` e navega para ele.
 * Retorna a object URL gerada (ou `null` quando navegou para `arquivo.url`).
 * Lança erro quando o arquivo é inválido (sem url e sem conteúdo), para o caller
 * tratar no `catch` (fechando a aba e exibindo mensagem).
 *
 * Observação: a object URL NÃO é revogada aqui — a aba ainda precisa dela para
 * renderizar o PDF (mesmo trade-off do preview inline).
 */
export const direcionarAbaArquivo = (win: Window | null, arquivo: ArquivoDto): string | null => {
  if (!win) return null
  if (arquivo?.url) {
    win.location.href = arquivo.url
    return null
  }
  const blobUrl = generatePdfUrl(arquivo?.contentBase64, arquivo?.type)
  if (!blobUrl) throw new Error('Arquivo inválido ou conteúdo não encontrado.')
  win.location.href = blobUrl
  return blobUrl
}

/** Fecha uma aba pré-aberta (usado em erro/validação inválida para não deixar aba órfã). */
export const fecharAba = (win: Window | null | undefined) => {
  try {
    win?.close()
  } catch {
    // Ignorar: alguns navegadores impedem close() de janelas não abertas por script.
  }
}

/**
 * Download de um ArquivoDto
 * @param arquivo ArquivoDto
 * @param opts.abaIOS aba pré-aberta no gesto de toque; quando presente e em iOS,
 *   a aba é redirecionada (url ou blob) em vez de usar `<a download>`, que o iOS
 *   ignora. Sem `opts`, o comportamento atual é preservado (não-regressão).
 */
export const downloadArquivo = async (arquivo: ArquivoDto, opts?: { abaIOS?: Window | null }) => {
  if (!arquivo) throw new Error('Arquivo inválido.')

  if (isIOS() && opts?.abaIOS) {
    direcionarAbaArquivo(opts.abaIOS, arquivo)
    return
  }

  if (arquivo.url) {
    window.open(arquivo.url, '_blank', 'noopener,noreferrer')
    return
  }

  if (!arquivo?.contentBase64) throw new Error('Arquivo inválido ou conteúdo não encontrado.')

  const byteCharacters = atob(arquivo.contentBase64)
  const byteNumbers = new Array(byteCharacters.length)
    .fill(0)
    .map((_, i) => byteCharacters.charCodeAt(i))
  const byteArray = new Uint8Array(byteNumbers)

  const blob = new Blob([byteArray], { type: arquivo.type })

  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', arquivo.name || 'arquivo')
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Formata data padrão em pt-BR
 * @param dateStr YYYY-MM-DD
 * @returns
 */
export const formatDateToPtBr = (dateStr?: string | null): string => {
  if (!dateStr) return ''
  const [year, month, day] = dateStr.split('-')
  return `${day}/${month}/${year}`
}

/**
 * Formata uma data para o valor de um <input type="date"> (YYYY-MM-DD), no fuso local.
 * `toISOString()` daria o dia seguinte à noite no Brasil.
 */
export const formatDateToInput = (date: string | Date): string => {
  if (typeof date === 'string') return date.slice(0, 10)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/**
 * Formata uma data para o valor de um <input type="datetime-local"> (YYYY-MM-DDTHH:mm), no fuso local.
 */
export const formatDateTimeToInput = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${formatDateToInput(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** Ordem de prioridade do documento principal de uma pessoa, com o rótulo curto de cada tipo. */
const PRIORIDADE_DOCUMENTO: Array<[DocumentoPessoaType, string]> = [
  [DocumentoPessoaType.Cpf, 'CPF'],
  [DocumentoPessoaType.Cns, 'CNS'],
  [DocumentoPessoaType.Passaporte, 'Passaporte'],
  [DocumentoPessoaType.BilheteIdentidadeAngola, 'BI'],
]

/**
 * Documento principal de uma pessoa ("CPF 123.456.789-00"): o primeiro que ela tiver, na ordem
 * CPF → CNS → Passaporte → BI. Usado como linha de apoio do nome nas listagens (funcionários,
 * pacientes). `undefined` quando não há nenhum.
 */
export const formatDocumentoPrincipal = (
  documentos?: Array<{ tipo?: string; numero?: string }>
): string | undefined => {
  for (const [tipo, rotulo] of PRIORIDADE_DOCUMENTO) {
    const documento = documentos?.find((doc) => doc.tipo === tipo)
    if (documento?.numero) return `${rotulo} ${documento.numero}`
  }
  return undefined
}

export const pdfBase64ToImages = async (pdfBase64: string): Promise<string[]> => {
  const { getDocument } = await import('pdfjs-dist')
  const pdfData = atob(pdfBase64)
  const len = pdfData.length
  const bytes = new Uint8Array(len)
  for (let i = 0; i < len; i++) {
    bytes[i] = pdfData.charCodeAt(i)
  }

  const pdf = await getDocument({ data: bytes }).promise
  const images: string[] = []

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i)
    const viewport = page.getViewport({ scale: 2 })
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')!
    canvas.width = viewport.width
    canvas.height = viewport.height
    await page.render({ canvas, canvasContext: ctx, viewport }).promise

    const base64Image = canvas.toDataURL('image/png')
    images.push(base64Image)
  }
  return images
}

/**
 * Gera URL de PDF a partir de base64
 * @param base64 Base64 do PDF
 * @param mimeType Tipo do arquivo, default 'application/pdf'
 * @returns URL do PDF
 */
export const generatePdfUrl = (
  base64: string | undefined,
  mimeType = 'application/pdf'
): string | null => {
  if (!base64) return null

  const byteCharacters = atob(base64)
  const byteNumbers = new Array(byteCharacters.length)
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i)
  }
  const byteArray = new Uint8Array(byteNumbers)
  const blob = new Blob([byteArray], { type: mimeType })
  return URL.createObjectURL(blob)
}

/**
 * Valida intervalo de datas e retorna erros/limites
 * @param startDate Data Inicial
 * @param endDate Data Final
 * @returns DateValidationResult
 */
export const validateDateRange = (
  startDate?: string,
  endDate?: string,
  options?: ValidateDateRangeOptions
): DateValidationResult => {
  const maxRange = options?.maxRangeDays
  let error = ''
  const limits: DateValidationResult['limits'] = {}

  if (startDate && endDate) {
    const start = new Date(startDate)
    const end = new Date(endDate)

    if (end < start) return { error: 'A data final não pode ser antes da inicial', limits }

    if (maxRange) {
      const diffDays = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))

      if (diffDays > maxRange)
        return { error: `O intervalo máximo permitido é de ${maxRange} dias`, limits }
    }
  }

  if (startDate) {
    limits.endMin = startDate

    if (maxRange) {
      const max = new Date(startDate)
      max.setDate(max.getDate() + maxRange)
      limits.endMax = max.toISOString().split('T')[0]
    }
  }

  if (endDate) {
    limits.startMax = endDate

    if (maxRange) {
      const min = new Date(endDate)
      min.setDate(min.getDate() - maxRange)
      limits.startMin = min.toISOString().split('T')[0]
    }
  }

  return { error, limits }
}

export const mapEnderecosToOptions = (enderecos: EnderecoList[]): IRadioOption[] => {
  return enderecos
    .filter((e) => e.ativo)
    .map((e) => ({
      id: e.id,
      descricao: (
        <span className='d-flex flex-column'>
          <span>{e.nome}</span>
          <span className='text-muted fs-7'>{e.completo}</span>
        </span>
      ),
      value: e.id.toString(),
    }))
}

/**
 * Valida uma data
 * @param val DATE string - YYYY-MM-DD
 * @param min string
 * @param max string
 * @returns string - vazia sem erro / com erro
 */
export const validateDate = (val: string, min?: string, max?: string): string => {
  if (!val) return ''

  // O Chrome aceita até 6 dígitos no ano; a regra do ano fica abaixo, com mensagem própria.
  // Segundos e fração são aceitos porque o `LocalDateTime` do backend chega assim.
  const pattern = /^\d{4,6}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?)?$/
  if (!pattern.test(val)) return 'Formato inválido. Use DD/MM/AAAA'

  const [datePart, timePart] = val.split('T')
  const [year, month, day] = datePart.split('-').map(Number)

  // "85" digitado no ano vira 0085; e `new Date(85, …)` cairia em 1985 e passaria como válido.
  if (year < 1000 || year > 9999) return 'Ano inválido (use 4 dígitos)'
  if (month < 1 || month > 12) return 'Mês inválido'
  if (day < 1 || day > 31) return 'Dia inválido'

  const date = new Date(year, month - 1, day)
  const isValidDate =
    date.getFullYear() === year && date.getMonth() + 1 === month && date.getDate() === day

  if (!isValidDate) return 'Data inválida'

  if (timePart) {
    const [hour, minute] = timePart.split(':').map(Number)

    if (hour < 0 || hour > 23) return 'Hora inválida'
    if (minute < 0 || minute > 59) return 'Minuto inválido'
  }

  const normalize = (v: string) => {
    if (v.length === 10) return `${v}T00:00`
    return v.slice(0, 16)
  }

  // Campo só de data (sem hora) não mostra "00:00" na mensagem.
  const comHora = val.length > 10
  const formatForDisplay = (v: string) => {
    const [datePart, timePart] = v.split('T')
    const [year, month, day] = datePart.split('-')

    if (!timePart || !comHora) return `${day}/${month}/${year}`

    return `${day}/${month}/${year} ${timePart}`
  }

  const normalizedVal = normalize(val)
  const normalizedMin = min ? normalize(min) : undefined
  const normalizedMax = max ? normalize(max) : undefined

  if (normalizedMin && normalizedVal < normalizedMin)
    return `Data deve ser maior ou igual a ${formatForDisplay(normalizedMin)}`

  if (normalizedMax && normalizedVal > normalizedMax)
    return `Data deve ser menor ou igual a ${formatForDisplay(normalizedMax)}`

  return ''
}

/**
 * Retorna o conteúdo de um arquivo tipo File
 * @param file File
 * @returns string - File Content
 */
export const readFileAsBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const base64 = (reader.result as string)?.split(',')[1]
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

/**
 * Normaliza um numero
 * @param value unknown
 * @returns number | null
 */
export const normalizeNumber = (value: unknown): number | null => {
  if (value === '' || value === null || value === undefined) return null
  return Number(value)
}

export const calcularIdade = (dataNascimento: string) => {
  const [ano, mes, dia] = dataNascimento.split('-').map(Number)
  const hoje = new Date()
  const nascimento = new Date(ano, mes - 1, dia)

  let idade = hoje.getFullYear() - nascimento.getFullYear()

  const fezAniversarioEsteAno =
    hoje.getMonth() > nascimento.getMonth() ||
    (hoje.getMonth() === nascimento.getMonth() && hoje.getDate() >= nascimento.getDate())

  if (!fezAniversarioEsteAno) idade--

  return idade
}

export const createValidation = (t: TFunction) => {
  const getMeta = (field?: string) => {
    if (!field) return { label: '', gender: 'male' }

    return {
      label: t(`${field}.label.lowercase`, undefined, field),
      gender: t(`${field}.gender`, undefined, 'male'),
    }
  }

  const requiredField = (field?: string, type: FieldType = 'input') => {
    const { label, gender } = getMeta(field)

    if (type === 'select') return t('validation.select', { name: label, gender })
    if (type === 'array')
      return t('validation.select.multiple', {
        name: label,
        gender,
      })
    return t('validation.input', { name: label, gender })
  }

  const minField = (min: number) =>
    t('validation.min', { name: min }, `Mínimo de ${min} caracteres`)

  const maxField = (max: number) =>
    t('validation.max', { name: max }, `Máximo de ${max} caracteres`)

  const minValueField = (min: number) =>
    t('validation.min_value', { name: min }, `Valor mínimo de R$ ${min}`)

  const maxValueField = (max: number) =>
    t('validation.max_value', { name: max }, `Valor máximo de R$ ${max}`)

  return {
    requiredField,
    minField,
    maxField,
    minValueField,
    maxValueField,
  }
}

/**
 *  Forma minutos em tempo de horas e minutos
 * @param duration Minutos
 * @returns string
 */
export const formatTimeDuration = (duration?: number) => {
  if (duration === undefined || duration === null) return '-'

  const totalMinutes = Math.max(0, Math.round(duration))
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`

  return `${minutes}min`
}

/**
 * Formata data e hora para pt-BR
 * @param date string
 * @param formattedDate string
 * @returns PT_BR String Date
 */
export const formatDateTime = (date?: string, formattedDate?: string) => {
  if (formattedDate) return formattedDate
  if (!date) return '-'

  const parsedDate = new Date(date)
  if (Number.isNaN(parsedDate.getTime())) return date

  return `${parsedDate.toLocaleDateString('pt-BR')} ${parsedDate.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })}`
}

/**
 * Formata somente hora e minuto (HH:mm) de uma data ISO
 * @param date string
 * @returns HH:mm, ou string vazia quando ausente/invalida
 */
export const formatHoraMinuto = (date?: string): string => {
  if (!date) return ''

  const parsedDate = new Date(date)
  if (Number.isNaN(parsedDate.getTime())) return ''

  return parsedDate.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

/**
 * Formata uma duracao em segundos (ex.: 50s, 1min 05s)
 * @param segundos number
 * @returns duracao formatada, ou string vazia quando ausente
 */
export const formatDuracaoSegundos = (segundos?: number): string => {
  if (segundos == null) return ''

  const minutos = Math.floor(segundos / 60)
  const resto = segundos % 60
  return minutos === 0 ? `${resto}s` : `${minutos}min ${resto.toString().padStart(2, '0')}s`
}

/**
 * Formata booleano para pt-BR
 * @param value boolean
 * @returns string
 */
export const formatBoolean = (value?: boolean) => {
  if (value === undefined || value === null) return '-'

  return value ? 'Sim' : 'Não'
}

export {
  createResponseContext,
  stringifyRequestQuery,
  parseRequestQuery,
  calculatedGroupingIsDisabled,
  calculateIsAllDataSelected,
  groupingOnSelect,
  groupingOnSelectAll,
  isNotEmpty,
  getParameterByName,
  createResponseContextSemPaginacao,
}
